import { useCallback } from 'react';
import { useAuthenticateUser } from '@xhype/card-app-sdk';
import { useMagic, useSmartWallet } from '@package/smart-wallet/providers';
import { useAuthStore } from '@package/smart-wallet/store';

import { useSignMessage } from './useSignMessage';

/* Types */
export type TAuthenticateProps = {
  onSuccess: () => void;
  onError?: (error: Error) => void;
};

/* Hook */
export const useAuthenticate = (props: TAuthenticateProps) => {
  const { onSuccess, onError } = props;
  const { smartAccountAddress } = useSmartWallet();
  const { signMessage } = useSignMessage();
  const { magic } = useMagic();
  const { logIn } = useAuthStore((state) => state);

  /* Mutations */
  const authenticateUserMutation = useAuthenticateUser({
    onError: (error: Error) => {
      console.error('Authentication failed:', error);
      if (onError) onError(error);
    },
    onSuccess: () => {
      console.log('User authenticated successfully, JWT token received');
    },
  });

  /* Callbacks */
  const authenticate = useCallback(async (): Promise<number> => {
    console.log('Authenticating user for Card App...');

    if (!smartAccountAddress) {
      throw new Error('Missing smartAccountAddress');
    }

    // Generate authentication message
    const message = `Sign this message to authenticate with Card App.\n\nAddress: ${smartAccountAddress}\nTimestamp: ${Date.now()}`;
    console.log('Generated authentication message');

    // Sign the message
    let signature: string;
    try {
      signature = await signMessage(message);
      console.log('Message signed successfully');
    } catch (error) {
      console.error('Failed to sign message:', error);
      if (onError) onError(error as Error);
      throw error;
    }

    // Authenticate with the backend using the SDK hook
    const authenticateData = await authenticateUserMutation.mutateAsync({
      address: smartAccountAddress,
      message,
      signature,
    });

    const userMagicInfo = await magic?.user.getInfo();
    const email = userMagicInfo?.email;

    if (!email) {
      const error = new Error('Email not found in Magic user info');
      console.error(error.message);
      if (onError) onError(error);
      throw error;
    }

    // Log in the user to the store
    logIn(smartAccountAddress, email, authenticateData.data.userId);
    console.log('User logged in to local store');

    // Wait a bit to ensure state is updated
    await new Promise((resolve) => setTimeout(resolve, 100));

    onSuccess();
    return authenticateData.data.userId;
  }, [
    smartAccountAddress,
    authenticateUserMutation,
    magic?.user,
    logIn,
    onSuccess,
    signMessage,
    onError,
  ]);

  /* Return */
  return {
    authenticate,
    isAuthenticating: authenticateUserMutation.isPending,
  };
};
